import { useCallback } from "react";
import { stringify } from "query-string";
import { ApiRoutes } from "../../../constants/apiRoutes";
import { useApi } from "../../../hooks/useApi";
import { urlReplacer } from "../../../utils";
import { EducationType } from "../../school/constants/types";
import { IFile } from "../../../components/UploadFile/types";

export function useApplicationQueries() {
  const { request } = useApi();

  return {
    uploadApplicationForm: useCallback(
      (file) => {
        let formData = new FormData();
        formData.append("file", file);
        return request({
          method: "POST",
          url: `${ApiRoutes.UPLOAD_STUDENT_APPLICATION_FORM}`,
          data: formData,
        });
      },
      [request],
    ),
    uploadRequiredDocs: useCallback(
      (file) => {
        let formData = new FormData();
        formData.append("file", file);
        return request({
          method: "POST",
          url: `${ApiRoutes.UPLOAD_STUDENT_REQUIRED_DOC}`,
          data: formData,
        });
      },
      [request],
    ),
    uploadExtraDocs: useCallback(
      (file) => {
        let formData = new FormData();
        formData.append("file", file);
        return request({
          method: "POST",
          url: `${ApiRoutes.UPLOAD_STUDENT_EXTRA_DOC}`,
          data: formData,
        });
      },
      [request],
    ),
    create: useCallback(
      async (data, educationType) => {
        const getUrl = (educationType: EducationType): string => {
          switch (educationType) {
            case EducationType.Language:
              return ApiRoutes.APPLICATION_LANGUAGE_COURSE;
            case EducationType.Secondary:
              return ApiRoutes.APPLICATION_SECONDARY;
            case EducationType.Higher:
              return ApiRoutes.APPLICATION_HIGHER;
            default:
              return "";
          }
        };

        return request({
          data,
          url: getUrl(educationType),
          method: "POST",
        });
      },
      [request],
    ),

    checkDuplication: useCallback(
      async ({ educationType, ...data }) => {
        const getUrl = (educationType: EducationType): string => {
          switch (educationType) {
            case EducationType.Language:
              return ApiRoutes.CHECK_APPLICATION_DUPLICATION_LANGUAGE_COURSE;
            case EducationType.Secondary:
              return ApiRoutes.CHECK_APPLICATION_DUPLICATION_SECONDARY;
            case EducationType.Higher:
              return ApiRoutes.CHECK_APPLICATION_DUPLICATION_HIGHER;
            default:
              return "";
          }
        };

        return request({
          data,
          url: getUrl(educationType),
          method: "POST",
        });
      },
      [request],
    ),

    getProgram: useCallback(
      async (id, educationType) => {
        const getUrl = (educationType: EducationType): string => {
          switch (educationType) {
            case EducationType.Language:
              return ApiRoutes.GET_LANGUAGE_COURSE_APPLY_AS;
            case EducationType.Secondary:
              return ApiRoutes.GET_PROGRAM_SECONDARY_APPLY_AS;
            case EducationType.Higher:
              return ApiRoutes.GET_PROGRAM_HIGHER_APPLY_AS;
            default:
              return "";
          }
        };

        return request({
          method: "GET",
          url: `${urlReplacer(getUrl(educationType), { id })}`,
        });
      },
      [request],
    ),

    getStudents: useCallback(
      async (id) => {
        return request({
          method: "GET",
          url: `${urlReplacer(ApiRoutes.GET_STUDENTS_FOR_APPLY_AS, { id })}`,
        });
      },
      [request],
    ),

    fetchStudentApplications: useCallback(
      async (
        queryParams: { [key: string]: string },
        educationType: EducationType,
      ) => {
        const getUrl = (educationType: EducationType): string => {
          switch (educationType) {
            case EducationType.Language:
              return `${
                ApiRoutes.APPLICATION_LANGUAGE_COURSE + stringify(queryParams)
              }`;
            case EducationType.Secondary:
              return `${
                ApiRoutes.APPLICATION_SECONDARY + stringify(queryParams)
              }`;
            case EducationType.Higher:
              return `${ApiRoutes.APPLICATION_HIGHER + stringify(queryParams)}`;
            default:
              return "";
          }
        };

        return request({
          method: "GET",
          url: getUrl(educationType),
        });
      },
      [request],
    ),

    updateApplicationStatus: useCallback(
      async (id, data, educationType) => {
        const getUrl = (educationType: EducationType): string => {
          switch (educationType) {
            case EducationType.Language:
              return ApiRoutes.UPDATE_APPLICATION_STATUS_LANGUAGE_COURSE;
            case EducationType.Secondary:
              return ApiRoutes.UPDATE_APPLICATION_STATUS_SECONDARY;
            case EducationType.Higher:
              return ApiRoutes.UPDATE_APPLICATION_STATUS_HIGHER;
            default:
              return "";
          }
        };

        return request({
          data,
          method: "PUT",
          url: `${urlReplacer(getUrl(educationType), { id })}`,
        });
      },
      [request],
    ),

    getViewData: useCallback(
      async (id, educationType) => {
        const getUrl = (educationType: EducationType): string => {
          switch (educationType) {
            case EducationType.Language:
              return ApiRoutes.GET_APPLICATION_LANGUAGE_COURSE;
            case EducationType.Secondary:
              return ApiRoutes.GET_APPLICATION_SECONDARY;
            case EducationType.Higher:
              return ApiRoutes.GET_APPLICATION_HIGHER;
            default:
              return "";
          }
        };

        return request({
          method: "GET",
          url: `${urlReplacer(getUrl(educationType), { id })}`,
        });
      },
      [request],
    ),

    cancelApplication: useCallback(
      async (id, educationType, data) => {
        const getUrl = (educationType: EducationType): string => {
          switch (educationType) {
            case EducationType.Language:
              return ApiRoutes.CANCEL_APPLICATION_LANGUAGE_COURSE;
            case EducationType.Secondary:
              return ApiRoutes.CANCEL_APPLICATION_SECONDARY;
            case EducationType.Higher:
              return ApiRoutes.CANCEL_APPLICATION_HIGHER;
            default:
              return "";
          }
        };

        return request({
          data: data,
          method: "PUT",
          url: `${urlReplacer(getUrl(educationType), { id })}`,
        });
      },
      [request],
    ),

    updateApplication: useCallback(
      async (data, educationType, id) => {
        const getUrl = (educationType: EducationType): string => {
          switch (educationType) {
            case EducationType.Language:
              return ApiRoutes.UPDATE_APPLICATION_LANGUAGE_COURSE;
            case EducationType.Secondary:
              return ApiRoutes.UPDATE_APPLICATION_SECONDARY;
            case EducationType.Higher:
              return ApiRoutes.UPDATE_APPLICATION_HIGHER;
            default:
              return "";
          }
        };

        return request({
          data,
          method: "PUT",
          url: `${urlReplacer(getUrl(educationType), { id })}`,
        });
      },
      [request],
    ),
  };
}
